import { useState, useEffect, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import type { WppChat } from "@/hooks/useWhatsAppChat";
import type { ContactAiInfo } from "@/hooks/useDashboardData";
import {
  useWhatsAppLabels,
  PIPELINE_STAGES,
  EXTRA_TAGS,
  getChatIdsFromLabel,
  type WppLabel,
} from "@/hooks/useWhatsAppLabels";

interface CsvCustomerPreview {
  phone: string;
  name: string;
  labelId?: string;
}

const PRESET_LABEL_STYLES: Record<string, { bg: string; text: string; border: string }> = {
  "Lead New": { bg: "bg-amber-50", text: "text-amber-800", border: "border-amber-200" },
  "In Contact": { bg: "bg-sky-50", text: "text-sky-800", border: "border-sky-200" },
  "Negotiating": { bg: "bg-orange-50", text: "text-orange-800", border: "border-orange-200" },
  "Customer": { bg: "bg-emerald-50", text: "text-emerald-800", border: "border-emerald-200" },
  "Lost": { bg: "bg-rose-50", text: "text-rose-800", border: "border-rose-200" },
  "VIP": { bg: "bg-yellow-100", text: "text-yellow-800", border: "border-yellow-200" },
  "Complaint": { bg: "bg-red-100", text: "text-red-800", border: "border-red-200" },
};

function getLabelBadgeStyle(label: WppLabel): { className?: string; style?: React.CSSProperties } {
  if (PRESET_LABEL_STYLES[label.name]) {
    const s = PRESET_LABEL_STYLES[label.name];
    return { className: `${s.bg} ${s.text} ${s.border}` };
  }
  if (label.hexColor) {
    return {
      style: {
        backgroundColor: `${label.hexColor}20`,
        color: label.hexColor,
        borderColor: `${label.hexColor}50`,
      },
    };
  }
  const hue = stringToHue(label.name);
  return {
    style: {
      backgroundColor: `oklch(0.95 0.05 ${hue})`,
      color: `oklch(0.40 0.18 ${hue})`,
      borderColor: `oklch(0.85 0.08 ${hue})`,
    },
  };
}

interface Country {
  code: string;
  name: string;
  flag: string;
  digits: number;
}

const COUNTRIES: Country[] = [
  { code: "+20", name: "Egypt", flag: "🇪🇬", digits: 10 },
  { code: "+966", name: "Saudi Arabia", flag: "🇸🇦", digits: 9 },
  { code: "+971", name: "UAE", flag: "🇦🇪", digits: 9 },
  { code: "+965", name: "Kuwait", flag: "🇰🇼", digits: 8 },
  { code: "+974", name: "Qatar", flag: "🇶🇦", digits: 8 },
  { code: "+973", name: "Bahrain", flag: "🇧🇭", digits: 8 },
  { code: "+968", name: "Oman", flag: "🇴🇲", digits: 8 },
  { code: "+962", name: "Jordan", flag: "🇯🇴", digits: 9 },
  { code: "+961", name: "Lebanon", flag: "🇱🇧", digits: 8 },
  { code: "+970", name: "Palestine", flag: "🇵🇸", digits: 9 },
  { code: "+964", name: "Iraq", flag: "🇮🇶", digits: 10 },
  { code: "+249", name: "Sudan", flag: "🇸🇩", digits: 9 },
  { code: "+212", name: "Morocco", flag: "🇲🇦", digits: 9 },
  { code: "+213", name: "Algeria", flag: "🇩🇿", digits: 9 },
  { code: "+216", name: "Tunisia", flag: "🇹🇳", digits: 8 },
  { code: "+218", name: "Libya", flag: "🇱🇾", digits: 9 },
  { code: "+963", name: "Syria", flag: "🇸🇾", digits: 9 },
  { code: "+967", name: "Yemen", flag: "🇾🇪", digits: 9 },
  { code: "+1", name: "United States", flag: "🇺🇸", digits: 10 },
  { code: "+44", name: "United Kingdom", flag: "🇬🇧", digits: 10 },
  { code: "+33", name: "France", flag: "🇫🇷", digits: 9 },
  { code: "+49", name: "Germany", flag: "🇩🇪", digits: 11 },
  { code: "+90", name: "Turkey", flag: "🇹🇷", digits: 10 },
  { code: "+91", name: "India", flag: "🇮🇳", digits: 10 },
];

function getChatName(chat: WppChat): string {
  return (
    chat.contact?.name ||
    chat.contact?.pushname ||
    chat.contact?.formattedName ||
    chat.contact?.shortName ||
    chat.groupMetadata?.subject ||
    chat.id?.user ||
    "Unknown"
  );
}

function getInitials(name: string): string {
  if (!name) return "?";
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2 && parts[0][0] && parts[parts.length - 1][0]) {
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }
  return name.slice(0, 2).toUpperCase() || "?";
}

function stringToHue(str: string): number {
  if (!str) return 0;
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  return Math.abs(hash) % 360;
}

function getPhoneHue(phone: string): number {
  return stringToHue(phone);
}

interface DisplayEntry {
  id: string;
  name: string;
  isManual: boolean;
  chat: WppChat | null;
  avatarUrl: string | null;
}

interface CrmPipelinePanelProps {
  sessionName: string;
  chats: WppChat[];
  selectedChat: WppChat | null;
  onSelectChat: (chat: WppChat) => void;
  contacts?: Record<string, ContactAiInfo>;
}

export default function CrmPipelinePanel({
  sessionName,
  chats,
  selectedChat,
  onSelectChat,
  contacts,
}: CrmPipelinePanelProps) {
  const { t } = useTranslation("dashboard");
  const {
    labels,
    loadingLabels,
    fetchLabels,
    applyLabel,
    deleteAllLabels,
  } = useWhatsAppLabels(sessionName);

  useEffect(() => {
    fetchLabels();
  }, [fetchLabels]);

  const [expandedEntry, setExpandedEntry] = useState<string | null>(null);
  const [activeLabelFilter, setActiveLabelFilter] = useState<string | "all">("all");
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [showAddCustomer, setShowAddCustomer] = useState(false);
  const [showImportCsv, setShowImportCsv] = useState(false);
  const [newPhone, setNewPhone] = useState("");
  const [newName, setNewName] = useState("");
  const [selectedLabelId, setSelectedLabelId] = useState<string>("");
  const [countryCode, setCountryCode] = useState("+20");
  const [showCountries, setShowCountries] = useState(false);
  const [addingCustomer, setAddingCustomer] = useState(false);
  const [addErr, setAddErr] = useState<string | null>(null);
  const [csvPreview, setCsvPreview] = useState<CsvCustomerPreview[]>([]);
  const [csvMsg, setCsvMsg] = useState<{ type: "success" | "warning" | "error"; text: string } | null>(null);
  const [csvLabelId, setCsvLabelId] = useState<string>("");

  const csvInputRef = useRef<HTMLInputElement>(null);

  const CsvParse = (text: string): { rows: Record<string, string>[]; errors: string[] } => {
    const errors: string[] = [];
    const normalized = text.replace(/\r\n/g, "\n").replace(/\r/g, "\n");
    const lines = normalized.split("\n").filter((l) => l.trim());
    if (lines.length === 0) { errors.push("File is empty."); return { rows: [], errors }; }
    const hasTabs = lines.some((l) => l.includes("\t"));
    const delimiter = hasTabs ? "\t" : ",";
    const parseLine = (line: string): string[] => {
      if (delimiter === "\t") return line.split("\t").map((v) => v.trim());
      const result: string[] = [];
      let current = ""; let inQuotes = false;
      for (let i = 0; i < line.length; i++) {
        const ch = line[i];
        if (inQuotes) {
          if (ch === '"') { if (i + 1 < line.length && line[i + 1] === '"') { current += '"'; i++; } else inQuotes = false; }
          else current += ch;
        } else {
          if (ch === '"') inQuotes = true;
          else if (ch === ',') { result.push(current.trim()); current = ""; }
          else current += ch;
        }
      }
      result.push(current.trim());
      return result;
    };
    const phoneKw = ["phone", "mobile", "tel", "number", "contact", "cell", "phone_number", "mobile_number", "phone number", "mobile number"];
    const hasHeader = (line: string) => parseLine(line).map((c) => c.toLowerCase()).some((c) => phoneKw.some((k) => c.includes(k)));
    let headerIdx = 0;
    if (!hasHeader(lines[0]) && lines.length > 1) { const idx = lines.findIndex((l, i) => i > 0 && hasHeader(l)); if (idx !== -1) headerIdx = idx; }
    const headers = parseLine(lines[headerIdx]);
    if (headers.length === 0 || headers.every((h) => !h)) { errors.push("No valid headers."); return { rows: [], errors }; }
    if (lines.length <= headerIdx + 1) { errors.push("No data rows."); return { rows: [], errors }; }
    const rows: Record<string, string>[] = [];
    lines.slice(headerIdx + 1).forEach((line, li) => {
      const vals = parseLine(line);
      if (vals.length !== headers.length) { errors.push(`Row ${li + 2}: column mismatch.`); return; }
      const row: Record<string, string> = {};
      headers.forEach((h, i) => { row[h] = vals[i] || ""; });
      rows.push(row);
    });
    return { rows, errors };
  };

  const findCol = (row: Record<string, string>, keywords: string[]): string | undefined => {
    const keys = Object.keys(row);
    const lowerKeys = keys.map((k) => k.toLowerCase().trim());
    for (const kw of keywords) {
      const idx = lowerKeys.findIndex((k) => k === kw || k.includes(kw));
      if (idx !== -1) return keys[idx];
    }
    return undefined;
  };

  const extractPhone = (row: Record<string, string>): string => {
    const phoneKeys = ["phone", "mobile", "tel", "number", "contact", "cell", "phone_number", "mobile_number", "phone number", "mobile number"];
    const key = findCol(row, phoneKeys);
    if (key) { const v = row[key].replace(/[^0-9]/g, ""); if (v) return v; }
    for (const k of Object.keys(row)) {
      const v = row[k].trim(); if (!v) continue;
      const digits = v.replace(/[^0-9]/g, "");
      if (digits.length >= 7 && digits.length >= v.replace(/[^0-9+]/g, "").length * 0.5) return digits;
    }
    return "";
  };

  const extractName = (row: Record<string, string>): string => {
    const nameKeys = ["name", "first_name", "last_name", "customer_name", "full_name", "display_name", "first name", "last name", "customer name", "full name"];
    const key = findCol(row, nameKeys);
    return key && row[key]?.trim() ? row[key].trim() : "";
  };

  const handleCsvFile = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setCsvMsg(null);
    const validTypes = ["text/csv", "application/vnd.ms-excel", "text/plain"];
    if (!validTypes.includes(file.type) && !/\.csv$/i.test(file.name)) {
      setCsvMsg({ type: "error", text: t("crm.importCsvInvalidFile") });
      e.target.value = ""; return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setCsvMsg({ type: "error", text: t("crm.importCsvReadError") });
      e.target.value = ""; return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const { rows, errors } = CsvParse(reader.result as string);
      if (rows.length === 0) {
        setCsvMsg({ type: "error", text: errors.length > 0 ? errors.slice(0, 3).join(" | ") : t("crm.importCsvEmpty") });
        e.target.value = ""; return;
      }
      const preview: CsvCustomerPreview[] = [];
      const seen = new Set<string>();
      rows.forEach((row) => {
        const phone = extractPhone(row);
        if (!phone || phone.length < 7 || phone.length > 15) return;
        if (seen.has(phone)) return;
        seen.add(phone);
        const name = extractName(row);
        preview.push({ phone, name: name || phone });
      });
      if (preview.length === 0) {
        setCsvMsg({ type: "warning", text: t("crm.importCsvNoValid") });
      } else {
        setCsvMsg({ type: "success", text: t("crm.importCsvWillImport", { count: preview.length, plural: preview.length !== 1 ? "s" : "" }) });
      }
      setCsvPreview(preview);
      e.target.value = "";
    };
    reader.onerror = () => setCsvMsg({ type: "error", text: t("crm.importCsvReadError") });
    reader.readAsText(file);
  }, [t]);

  const handleImportCsv = useCallback(async () => {
    if (csvPreview.length === 0) return;
    const targetLabelId = csvLabelId || labels[0]?.id;
    if (!targetLabelId) return;

    setAddingCustomer(true);
    try {
      for (const item of csvPreview) {
        const chatId = item.phone.includes("@") ? item.phone : `${item.phone}@c.us`;
        await applyLabel(chatId, targetLabelId, "add");
      }

      setCsvPreview([]);
      setCsvMsg(null);
      setShowImportCsv(false);
    } catch (err) {
      console.error("Failed to import CSV contacts:", err);
    } finally {
      setAddingCustomer(false);
    }
  }, [csvPreview, csvLabelId, labels, applyLabel]);

  const downloadCsvTemplate = useCallback(() => {
    const csv = "phone,name\n+201234567890,Ahmed Mohamed\n+201098765432,Sara Ali";
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "crm_import_template.csv"; a.click();
    URL.revokeObjectURL(url);
  }, []);

  const displayEntries: DisplayEntry[] = (() => {
    const seenIds = new Set<string>();
    const entries: DisplayEntry[] = [];

    for (const chat of chats) {
      if (!chat?.id?._serialized) continue;
      const id = chat.id._serialized;
      seenIds.add(id);
      const phonePart = id.split("@")[0];
      if (phonePart) seenIds.add(phonePart);

      entries.push({
        id,
        name: getChatName(chat),
        isManual: false,
        chat,
        avatarUrl: chat.contact?.profilePicThumbObj?.img || null,
      });
    }

    if (Array.isArray(labels)) {
      for (const label of labels) {
        if (!label) continue;
        const chatIds = getChatIdsFromLabel(label);
        for (const rawId of chatIds) {
          if (!rawId) continue;
          const phonePart = rawId.split("@")[0];
          if (seenIds.has(rawId) || (phonePart && seenIds.has(phonePart))) continue;

          const id = rawId.includes("@") ? rawId : `${rawId}@c.us`;
          seenIds.add(id);
          if (phonePart) seenIds.add(phonePart);

          entries.push({
            id,
            name: phonePart || rawId,
            isManual: true,
            chat: null,
            avatarUrl: null,
          });
        }
      }
    }

    return entries;
  })();

  const getAssignedLabels = (entry: DisplayEntry): WppLabel[] => {
    if (!entry || !Array.isArray(labels) || labels.length === 0) return [];
    const assignedMap = new Map<string, WppLabel>();

    // Check WppChat labels property from WPPConnect if available
    if (entry.chat) {
      const rawChat = entry.chat as any;
      const chatLabelArray = rawChat.labels || rawChat.labelIds || rawChat.label;
      if (Array.isArray(chatLabelArray)) {
        for (const item of chatLabelArray) {
          const itemStr = typeof item === "object" && item !== null ? String(item.id || item.name) : String(item);
          const matched = labels.find((l) => String(l.id) === itemStr || l.name === itemStr);
          if (matched) assignedMap.set(matched.id, matched);
        }
      }
    }

    // Check labels collection from get-all-labels
    const chatId = entry.id;
    const phonePart = chatId.split("@")[0];

    for (const label of labels) {
      if (!label) continue;
      const ids = getChatIdsFromLabel(label);
      if (ids.some((id) => id === chatId || (phonePart && id.split("@")[0] === phonePart))) {
        assignedMap.set(label.id, label);
      }
    }

    return Array.from(assignedMap.values());
  };

  const filteredEntries = displayEntries.filter((entry) => {
    if (activeLabelFilter === "all") return true;
    const assigned = getAssignedLabels(entry);
    return assigned.some((l) => l.id === activeLabelFilter || l.name === activeLabelFilter);
  });

  const handleToggleLabel = useCallback(
    async (entry: DisplayEntry, label: WppLabel) => {
      const assigned = getAssignedLabels(entry);
      const hasLabel = assigned.some((l) => l.id === label.id);
      await applyLabel(entry.id, label.id, hasLabel ? "remove" : "add");
    },
    [labels, applyLabel]
  );

  const handleReset = useCallback(async () => {
    setShowResetConfirm(false);
    await deleteAllLabels();
  }, [deleteAllLabels]);

  const handleAddCustomer = useCallback(async () => {
    setAddErr(null);
    const selectedCountry = COUNTRIES.find((c) => c.code === countryCode) || COUNTRIES[0];
    const phone = newPhone.trim().replace(/\D/g, "");

    if (!phone || phone.length !== selectedCountry.digits) {
      setAddErr(t("crm.invalidPhone", { digits: selectedCountry.digits }));
      return;
    }

    const fullPhone = countryCode.replace("+", "") + phone;
    const chatId = `${fullPhone}@c.us`;

    const labelToApply = selectedLabelId || (labels.length > 0 ? labels[0].id : "");
    if (!labelToApply) {
      setAddErr("No WhatsApp labels found to assign");
      return;
    }

    setAddingCustomer(true);
    try {
      const ok = await applyLabel(chatId, labelToApply, "add");
      if (ok) {
        setNewPhone("");
        setNewName("");
        setSelectedLabelId("");
        setCountryCode("+20");
        setShowAddCustomer(false);
      } else {
        setAddErr("Failed to add label on WhatsApp");
      }
    } catch {
      setAddErr("Failed to add label on WhatsApp");
    } finally {
      setAddingCustomer(false);
    }
  }, [newPhone, newName, selectedLabelId, countryCode, labels, applyLabel, t]);

  const totalTracked = displayEntries.filter((e) => getAssignedLabels(e).length > 0).length;

  return (
    <div className="flex flex-col h-full">
      <div className="flex-shrink-0 px-4 py-3 flex items-center justify-between border-b border-background-200/60 bg-background-50">
        <div className="flex items-center gap-3">
          <h3 className="font-heading text-base text-foreground-950">{t("crm.title", "CRM Pipeline")}</h3>
          <span className="text-[11px] font-medium text-foreground-400 bg-background-100 px-2.5 py-0.5 rounded-full">
            {totalTracked} {t("crm.tracked", "tracked")}
          </span>
        </div>
        <div className="flex items-center gap-1.5">
          <button
            onClick={() => {
              setShowAddCustomer(true);
              setAddErr(null);
              setNewPhone("");
              setNewName("");
              setSelectedLabelId(labels[0]?.id || "");
              setCountryCode("+20");
            }}
            className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-primary-50 text-foreground-500 hover:text-primary-600 cursor-pointer transition-colors"
            title={t("crm.addCustomer", "Add Customer")}
          >
            <i className="ri-user-add-line text-sm"></i>
          </button>
          <button
            onClick={() => {
              setShowImportCsv(true);
              setCsvPreview([]);
              setCsvMsg(null);
            }}
            className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-accent-50 text-foreground-500 hover:text-accent-600 cursor-pointer transition-colors"
            title={t("crm.importCsv", "Import CSV")}
          >
            <i className="ri-upload-cloud-2-line text-sm"></i>
          </button>
          <button
            onClick={fetchLabels}
            disabled={loadingLabels}
            className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-background-100 text-foreground-500 cursor-pointer transition-colors"
            title={t("crm.refreshLabels", "Refresh Labels")}
          >
            <i className={`ri-refresh-line text-sm ${loadingLabels ? "animate-spin" : ""}`}></i>
          </button>
        </div>
      </div>

      <div className="flex-shrink-0 px-3 py-2 border-b border-background-200/40 bg-background-50">
        <div className="flex items-center gap-1.5 overflow-x-auto">
          <button
            onClick={() => setActiveLabelFilter("all")}
            className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${
              activeLabelFilter === "all"
                ? "bg-foreground-900 text-white"
                : "bg-background-100 text-foreground-600 hover:bg-background-200"
            }`}
          >
            {t("crm.all", "All")} ({totalTracked})
          </button>
          {labels.map((label) => {
            const count =
              displayEntries.filter((entry) =>
                getAssignedLabels(entry).some((l) => l.id === label.id || l.name === label.name)
              ).length ||
              label.count ||
              getChatIdsFromLabel(label).length ||
              0;
            const isSelected = activeLabelFilter === label.id;
            const badgeStyle = getLabelBadgeStyle(label);
            return (
              <button
                key={label.id}
                onClick={() => setActiveLabelFilter(isSelected ? "all" : label.id)}
                style={isSelected ? badgeStyle.style : undefined}
                className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${
                  isSelected
                    ? badgeStyle.className || "bg-primary-100 text-primary-800 border border-primary-300"
                    : "bg-background-100 text-foreground-600 hover:bg-background-200"
                }`}
              >
                {label.name} ({count})
              </button>
            );
          })}
        </div>
      </div>

      {/* Entry list */}
      <div className="flex-1 overflow-y-auto">
        {filteredEntries.length === 0 && (
          <div className="px-4 py-10 text-center">
            <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-background-100 flex items-center justify-center">
              <i className="ri-price-tag-3-line text-foreground-400 text-lg"></i>
            </div>
            <p className="text-foreground-500 text-xs mb-3">
              {labels.length === 0
                ? "No labels found on this WhatsApp account yet."
                : "No contacts found under this label."}
            </p>
          </div>
        )}

        {filteredEntries.map((entry) => {
          const assignedLabels = getAssignedLabels(entry);
          const isExpanded = expandedEntry === entry.id;
          const isThisSelected = !entry.isManual && selectedChat?.id._serialized === entry.id;
          const initials = getInitials(entry.name);
          const hue = entry.isManual ? getPhoneHue(entry.name) : stringToHue(entry.id);

          return (
            <div
              key={entry.id}
              className={`border-b border-background-100/60 transition-colors ${
                isThisSelected ? "bg-background-200/60" : "hover:bg-background-100/30"
              }`}
            >
              <div
                className="flex items-start gap-2.5 px-3 py-2.5 cursor-pointer"
                onClick={() => setExpandedEntry(isExpanded ? null : entry.id)}
              >
                {/* Avatar */}
                <div
                  className="flex-shrink-0 self-start mt-0.5"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (!entry.isManual && entry.chat) {
                      onSelectChat(entry.chat);
                    }
                  }}
                >
                  {entry.avatarUrl ? (
                    <div className={`w-10 h-10 rounded-full overflow-hidden ${!entry.isManual && entry.chat ? "cursor-pointer hover:ring-2 hover:ring-primary-300 hover:ring-offset-1 transition-all" : ""}`}>
                      <img src={entry.avatarUrl} alt={entry.name} className="w-full h-full object-cover" />
                    </div>
                  ) : (
                    <div
                      className={`w-10 h-10 rounded-full flex items-center justify-center text-white text-xs font-semibold ${!entry.isManual && entry.chat ? "cursor-pointer hover:ring-2 hover:ring-primary-300 hover:ring-offset-1 transition-all" : ""}`}
                      style={{ background: `oklch(0.55 0.15 ${hue})` }}
                    >
                      {initials}
                    </div>
                  )}
                </div>

                {/* Info & Badges */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-medium text-foreground-950 truncate">{entry.name}</span>
                    {entry.isManual && (
                      <span className="text-[10px] px-1.5 py-0.5 rounded-full border font-medium whitespace-nowrap bg-background-100 text-foreground-500 border-background-200">
                        {t("crm.manualEntry", "Manual")}
                      </span>
                    )}
                  </div>

                  <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                    {assignedLabels.length > 0 ? (
                      assignedLabels.map((lbl) => {
                        const badgeStyle = getLabelBadgeStyle(lbl);
                        return (
                          <span
                            key={lbl.id}
                            style={badgeStyle.style}
                            className={`text-[10px] px-2 py-0.5 rounded-full border font-medium whitespace-nowrap ${
                              badgeStyle.className || "bg-background-100 text-foreground-700 border-background-200"
                            }`}
                          >
                            {lbl.name}
                          </span>
                        );
                      })
                    ) : (
                      <span className="text-[11px] text-foreground-400 block">{t("crm.untracked", "No Labels")}</span>
                    )}
                  </div>

                  {/* AI Reason / Comment */}
                  {(() => {
                    const phonePart = entry.id.split("@")[0];
                    const aiInfo = contacts?.[entry.id] || (phonePart ? contacts?.[`${phonePart}@c.us`] || contacts?.[phonePart] : undefined);
                    if (!aiInfo?.reason) return null;
                    return (
                      <div className="mt-1.5 inline-flex items-center gap-1.5 text-[11px] text-foreground-700 bg-background-100/80 border border-background-200/80 rounded-xl px-2.5 py-1 max-w-full">
                        <span className="flex items-center justify-center w-4 h-4 rounded-full bg-primary-100 text-primary-600 flex-shrink-0 text-[10px]">
                          <i className="ri-sparkling-fill"></i>
                        </span>
                        <span className="leading-tight truncate">{aiInfo.reason}</span>
                      </div>
                    );
                  })()}
                </div>

                {/* Actions */}
                <div className="flex items-center gap-1">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setExpandedEntry(isExpanded ? null : entry.id);
                    }}
                    className={`w-7 h-7 flex items-center justify-center rounded-full transition-colors cursor-pointer ${
                      isExpanded ? "bg-foreground-100 text-foreground-700" : "hover:bg-background-100 text-foreground-400"
                    }`}
                    title={t("crm.manageStage", "Manage Labels")}
                  >
                    <i className={`ri-price-tag-3-line text-sm transition-transform ${isExpanded ? "scale-110 text-primary-600" : ""}`}></i>
                  </button>
                </div>
              </div>

              {/* Expanded WhatsApp label toggles */}
              {isExpanded && (
                <div className="px-3 pb-3 pt-2 border-t border-background-100/40 bg-background-100/20">
                  <p className="text-[10px] text-foreground-400 uppercase tracking-wide mb-1.5">WhatsApp Labels</p>
                  {labels.length === 0 ? (
                    <p className="text-xs text-foreground-400">No WhatsApp labels found.</p>
                  ) : (
                    <div className="flex flex-wrap gap-1.5">
                      {labels.map((lbl) => {
                        const isActive = assignedLabels.some((l) => l.id === lbl.id);
                        const badgeStyle = getLabelBadgeStyle(lbl);
                        return (
                          <button
                            key={lbl.id}
                            onClick={() => handleToggleLabel(entry, lbl)}
                            style={isActive ? badgeStyle.style : undefined}
                            className={`px-2.5 py-1 rounded-full text-[11px] font-medium transition-all cursor-pointer whitespace-nowrap border ${
                              isActive
                                ? badgeStyle.className || "bg-primary-100 text-primary-800 border-primary-300"
                                : "bg-background-50 text-foreground-500 border-background-200 hover:bg-background-100"
                            }`}
                          >
                            <i className={`${isActive ? "ri-check-line" : "ri-add-line"} text-[10px] mr-1`}></i>
                            {lbl.name}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}


      </div>

      {/* Add Customer Modal */}
      {showAddCustomer && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={() => setShowAddCustomer(false)}>
          <div className="bg-background-50 rounded-2xl p-6 max-w-md w-full mx-4 shadow-lg" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-primary-50 flex items-center justify-center">
                <i className="ri-user-add-line text-primary-500 text-lg"></i>
              </div>
              <div>
                <h4 className="font-heading text-base text-foreground-950">{t("crm.addCustomerTitle", "Add New Customer")}</h4>
                <p className="text-foreground-500 text-[11px]">{t("crm.addCustomerDesc", "Add a customer that hasn't messaged yet to track them in the Pipeline.")}</p>
              </div>
            </div>

            <div className="space-y-3.5">
              {/* Country Code + Phone */}
              <div>
                <label className="block text-xs font-medium text-foreground-700 mb-1">{t("crm.countryCodeLabel", "Country Code")}</label>
                <div className="flex gap-2">
                  <div className="relative">
                    <button
                      type="button"
                      onClick={() => setShowCountries(!showCountries)}
                      className={`h-full flex items-center gap-1.5 px-3 py-2.5 rounded-xl border text-sm font-medium cursor-pointer whitespace-nowrap transition-colors ${
                        showCountries
                          ? "border-primary-400 bg-background-50 text-foreground-950"
                          : "border-background-200 bg-background-50 text-foreground-950 hover:bg-background-100"
                      }`}
                    >
                      <span className="text-base leading-none">{COUNTRIES.find((c) => c.code === countryCode)?.flag || "🇪🇬"}</span>
                      <span>{countryCode}</span>
                      <i className={`${showCountries ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"} text-foreground-400 text-xs`}></i>
                    </button>

                    {showCountries && (
                      <>
                        <div className="fixed inset-0 z-10" onClick={() => setShowCountries(false)}></div>
                        <div className="absolute top-full left-0 mt-1 z-20 w-56 max-h-52 overflow-y-auto bg-background-50 border border-background-200 rounded-xl shadow-lg py-1">
                          {COUNTRIES.map((country) => (
                            <button
                              key={country.code}
                              type="button"
                              onClick={() => {
                                setCountryCode(country.code);
                                setNewPhone("");
                                setAddErr(null);
                                setShowCountries(false);
                              }}
                              className={`w-full flex items-center gap-2.5 px-3 py-2 text-sm cursor-pointer transition-colors hover:bg-background-100 ${
                                country.code === countryCode
                                  ? "bg-primary-100 text-primary-700 font-medium"
                                  : "text-foreground-800"
                              }`}
                            >
                              <span className="text-base leading-none">{country.flag}</span>
                              <span className="flex-1 text-left">{country.name}</span>
                              <span className="text-foreground-400 text-xs">{country.code}</span>
                            </button>
                          ))}
                        </div>
                      </>
                    )}
                  </div>

                  <div className="flex-1 relative">
                    {(() => {
                      const selectedCountry = COUNTRIES.find((c) => c.code === countryCode) || COUNTRIES[0];
                      const digitPlaceholder = Array.from({ length: selectedCountry.digits }, (_, i) => ((i % 9) + 1).toString()).join("");
                      const remainingDigits = selectedCountry.digits - newPhone.replace(/\D/g, "").length;
                      return (
                        <>
                          <input
                            type="tel"
                            value={newPhone}
                            onChange={(e) => {
                              const cleaned = e.target.value.replace(/\D/g, "").replace(/^0+/, "").slice(0, selectedCountry.digits);
                              setNewPhone(cleaned);
                              setAddErr(null);
                            }}
                            placeholder={digitPlaceholder}
                            maxLength={selectedCountry.digits}
                            className="w-full px-3 py-2.5 rounded-xl border border-background-200 bg-background-50 text-sm text-foreground-950 placeholder:text-foreground-300 focus:outline-none focus:border-primary-300 transition-colors pr-14"
                            autoFocus
                          />
                          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-foreground-400 pointer-events-none whitespace-nowrap">
                            {remainingDigits > 0
                              ? remainingDigits === 1
                                ? t("crm.digitsLeftOne", "1 digit left")
                                : t("crm.digitsLeft", { count: remainingDigits })
                              : newPhone.replace(/\D/g, "").length > 0
                                ? t("crm.phoneValid", "✓")
                                : ""}
                          </span>
                        </>
                      );
                    })()}
                  </div>
                </div>
                {(() => {
                  const selectedCountry = COUNTRIES.find((c) => c.code === countryCode) || COUNTRIES[0];
                  const phoneDigits = newPhone.replace(/\D/g, "");
                  const remainingDigits = selectedCountry.digits - phoneDigits.length;
                  if (!addErr && phoneDigits.length > 0 && phoneDigits.length < selectedCountry.digits) {
                    return (
                      <p className="text-foreground-400 text-[10px] mt-1">
                        {remainingDigits === 1
                          ? t("crm.enterMoreDigitsOne", "Enter 1 more digit")
                          : t("crm.enterMoreDigits", { count: remainingDigits })}
                      </p>
                    );
                  }
                  return null;
                })()}
              </div>

              {/* Name */}
              <div>
                <label className="block text-xs font-medium text-foreground-700 mb-1">{t("crm.nameLabel", "Customer Name")}</label>
                <input
                  type="text"
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  placeholder={t("crm.namePlaceholder", "Enter customer name")}
                  className="w-full px-3 py-2.5 rounded-xl border border-background-200 bg-background-50 text-sm text-foreground-950 placeholder:text-foreground-300 focus:outline-none focus:border-primary-300 transition-colors"
                />
              </div>

              {/* WhatsApp Label Selector */}
              <div>
                <label className="block text-xs font-medium text-foreground-700 mb-1">WhatsApp Label</label>
                <div className="flex flex-wrap gap-1.5">
                  {labels.map((lbl) => {
                    const isSelected = selectedLabelId === lbl.id;
                    const badgeStyle = getLabelBadgeStyle(lbl);
                    return (
                      <button
                        key={lbl.id}
                        type="button"
                        onClick={() => setSelectedLabelId(lbl.id)}
                        style={isSelected ? badgeStyle.style : undefined}
                        className={`px-2.5 py-1.5 rounded-full text-[11px] font-medium transition-all cursor-pointer whitespace-nowrap border ${
                          isSelected
                            ? badgeStyle.className || "bg-primary-100 text-primary-800 border-primary-400"
                            : "bg-background-50 text-foreground-500 border-background-200 hover:bg-background-100"
                        }`}
                      >
                        {lbl.name}
                      </button>
                    );
                  })}
                </div>
              </div>

              {addErr && (
                <div className="p-2.5 rounded-lg bg-red-50 text-red-700 text-xs flex items-start gap-2">
                  <i className="ri-error-warning-line flex-shrink-0 mt-0.5"></i>
                  <span>{addErr}</span>
                </div>
              )}
            </div>

            <div className="flex gap-3 justify-end mt-5">
              <button
                onClick={() => setShowAddCustomer(false)}
                className="px-4 py-2 rounded-full text-sm text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
              >
                {t("crm.cancel", "Cancel")}
              </button>
              <button
                onClick={handleAddCustomer}
                className="px-5 py-2 rounded-full bg-primary-500 text-white text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap flex items-center gap-1.5"
              >
                <i className="ri-check-line text-sm"></i>
                {t("crm.addBtn", "Add to Pipeline")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* CSV Import Modal */}
      <input
        type="file"
        ref={csvInputRef}
        className="hidden"
        accept=".csv"
        onChange={handleCsvFile}
      />
      {showImportCsv && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={() => { setShowImportCsv(false); setCsvPreview([]); setCsvMsg(null); }}>
          <div className="bg-background-50 rounded-2xl p-6 max-w-lg w-full mx-4 max-h-[90vh] overflow-y-auto shadow-lg" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-accent-100 flex items-center justify-center">
                <i className="ri-file-upload-line text-accent-600 text-lg"></i>
              </div>
              <div>
                <h4 className="font-heading text-base text-foreground-950">{t("crm.importCsvTitle")}</h4>
                <p className="text-foreground-500 text-[11px]">{t("crm.importCsvDesc")}</p>
              </div>
              <button
                onClick={() => { setShowImportCsv(false); setCsvPreview([]); setCsvMsg(null); }}
                className="ml-auto w-8 h-8 flex items-center justify-center rounded-full hover:bg-background-100 text-foreground-400 cursor-pointer transition-colors"
              >
                <i className="ri-close-line"></i>
              </button>
            </div>

            {/* Target Label Selector */}
            <div className="mb-4">
              <label className="block text-xs font-medium text-foreground-700 mb-1.5">
                Target WhatsApp Label:
              </label>
              <div className="flex flex-wrap gap-1.5">
                {labels.map((lbl) => {
                  const isSelected = (csvLabelId || labels[0]?.id) === lbl.id;
                  const badgeStyle = getLabelBadgeStyle(lbl);
                  return (
                    <button
                      key={lbl.id}
                      type="button"
                      onClick={() => setCsvLabelId(lbl.id)}
                      style={isSelected ? badgeStyle.style : undefined}
                      className={`px-2.5 py-1.5 rounded-full text-[11px] font-medium transition-all cursor-pointer whitespace-nowrap border ${
                        isSelected
                          ? badgeStyle.className || "bg-primary-100 text-primary-800 border-primary-400 font-semibold"
                          : "bg-background-50 text-foreground-500 border-background-200 hover:bg-background-100"
                      }`}
                    >
                      {lbl.name}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Column info */}
            <div className="mb-4 p-3 rounded-xl bg-background-100 border border-background-200/50">
              <p className="text-xs font-semibold text-foreground-800 mb-2">{t("crm.importCsvColumns")}</p>
              <div className="grid grid-cols-2 gap-2 text-[11px]">
                <div className="flex items-start gap-1.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-red-400 mt-1.5 flex-shrink-0"></span>
                  <span className="text-foreground-700">{t("crm.importCsvColumnPhone")}</span>
                </div>
                <div className="flex items-start gap-1.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-amber-400 mt-1.5 flex-shrink-0"></span>
                  <span className="text-foreground-600">{t("crm.importCsvColumnName")}</span>
                </div>
              </div>
            </div>

            {/* Template download */}
            <button
              onClick={downloadCsvTemplate}
              className="w-full mb-4 px-4 py-2.5 rounded-xl border border-dashed border-background-300 bg-background-50 hover:bg-background-100 text-foreground-600 text-xs font-medium cursor-pointer transition-colors flex items-center justify-center gap-2"
            >
              <i className="ri-download-line text-sm"></i>
              {t("crm.importCsvDownloadTemplate")}
            </button>

            {/* Upload area */}
            {csvPreview.length === 0 && (
              <div
                onClick={() => csvInputRef.current?.click()}
                className="mb-4 border-2 border-dashed border-background-200 hover:border-accent-300 rounded-xl p-8 text-center cursor-pointer transition-colors bg-background-50 hover:bg-accent-50/30"
              >
                <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-background-100 flex items-center justify-center">
                  <i className="ri-upload-cloud-2-line text-foreground-400 text-xl"></i>
                </div>
                <p className="text-sm text-foreground-600 font-medium">{t("crm.importCsvDropHere")}</p>
                <p className="text-xs text-foreground-400 mt-1">{t("crm.importCsvCsvOnly")}</p>
              </div>
            )}

            {/* Status message */}
            {csvMsg && (
              <div className={`mb-4 p-3 rounded-xl text-xs flex items-start gap-2 ${
                csvMsg.type === "success" ? "bg-accent-50 border border-accent-200/50 text-accent-800" :
                csvMsg.type === "warning" ? "bg-amber-50 border border-amber-200/50 text-amber-800" :
                "bg-red-50 border border-red-200/50 text-red-700"
              }`}>
                <i className={`flex-shrink-0 text-sm ${csvMsg.type === "success" ? "ri-check-line" : csvMsg.type === "warning" ? "ri-error-warning-line" : "ri-close-circle-line"}`}></i>
                <span className="flex-1">{csvMsg.text}</span>
                <button onClick={() => setCsvMsg(null)} className="w-5 h-5 flex items-center justify-center rounded-full hover:bg-black/10 text-current cursor-pointer flex-shrink-0">
                  <i className="ri-close-line text-[10px]"></i>
                </button>
              </div>
            )}

            {/* Preview table */}
            {csvPreview.length > 0 && (
              <>
                <div className="mb-3 flex items-center justify-between">
                  <h5 className="text-xs font-semibold text-foreground-800">
                    {t("crm.importCsvPreviewTitle", { count: csvPreview.length, plural: csvPreview.length !== 1 ? "s" : "" })}
                  </h5>
                  <button
                    onClick={() => { setCsvPreview([]); setCsvMsg(null); }}
                    className="text-[11px] text-foreground-500 hover:text-foreground-700 cursor-pointer transition-colors flex items-center gap-1"
                  >
                    <i className="ri-refresh-line text-[11px]"></i>
                    {t("crm.cancel", "Cancel")}
                  </button>
                </div>
                <div className="max-h-48 overflow-y-auto rounded-xl border border-background-200/50 mb-4">
                  <table className="w-full text-xs">
                    <thead className="sticky top-0 bg-background-100 text-left">
                      <tr>
                        <th className="px-3 py-2 text-foreground-500 font-medium">{t("crm.importCsvColumnPhone")}</th>
                        <th className="px-3 py-2 text-foreground-500 font-medium">{t("crm.importCsvColumnName")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {csvPreview.slice(0, 50).map((cust, idx) => (
                        <tr key={`${cust.phone}_${idx}`} className="border-t border-background-100/60">
                          <td className="px-3 py-2 font-mono text-foreground-900">{cust.phone}</td>
                          <td className="px-3 py-2 text-foreground-700">{cust.name}</td>
                        </tr>
                      ))}
                      {csvPreview.length > 50 && (
                        <tr>
                          <td colSpan={2} className="px-3 py-2 text-center text-foreground-400">
                            ... and {csvPreview.length - 50} more
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </>
            )}

            {/* Action buttons */}
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => { setShowImportCsv(false); setCsvPreview([]); setCsvMsg(null); }}
                className="px-4 py-2 rounded-full text-sm text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
              >
                {t("crm.cancel", "Cancel")}
              </button>
              {csvPreview.length > 0 && (
                <button
                  onClick={handleImportCsv}
                  className="px-5 py-2 rounded-full bg-accent-500 text-white text-sm font-medium hover:bg-accent-600 transition-colors cursor-pointer whitespace-nowrap flex items-center gap-1.5"
                >
                  <i className="ri-check-line text-sm"></i>
                  {t("crm.importCsvBtn", { count: csvPreview.length, plural: csvPreview.length !== 1 ? "s" : "" })}
                </button>
              )}
            </div>
          </div>
        </div>
      )}


    </div>
  );
}